import React from 'react';

const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'Outfit', sans-serif";

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#0C0A14] text-white">
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8 md:py-10">
        <a href="/" className="inline-flex items-center gap-2 text-white/45 hover:text-white/80 text-sm mb-6" style={{ fontFamily: SANS }}>
          Back to Refract
        </a>

        <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl overflow-hidden">
      <header className="border-b border-white/10 bg-transparent">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 md:px-8 py-6">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-white/35" style={{ fontFamily: SANS }}>Refract</p>
            <h1 className="text-4xl md:text-5xl font-light" style={{ fontFamily: SERIF }}>Privacy Policy</h1>
          </div>
          <a
            href="/"
            className="rounded-xl border border-white/15 px-4 py-2 text-sm text-white/65 transition hover:border-white/30 hover:text-white"
            style={{ fontFamily: SANS }}
          >
            Back to Refract
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 md:px-8 py-8 leading-relaxed text-white/70" style={{ fontFamily: SANS }}>
        <p className="text-sm text-white/45 mb-1">Effective date: February 12, 2026</p>
        <p className="text-sm text-white/45 mb-8">Last updated: February 14, 2026</p>

        <p className="mb-8 text-white/85">
          Refract helps you rediscover photos and videos in your Google Drive and local device.
          Your media stays local by default; optional Cloud Backup (Pro/Power) stores a copy on our infrastructure for cross-device access.
          This policy explains, in plain language, exactly what data we touch, what we store, and what we don't.
        </p>

        {/* ── 1. The short version ── */}
        <section className="mb-12 rounded-xl border border-white/10 bg-white/[0.03] p-6">
          <h2 className="text-lg font-semibold text-white mb-4">The short version</h2>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="text-emerald-400 mt-0.5">✓</span>
              <span><strong className="text-white">Your photos and videos are never uploaded to our servers.</strong> Media stays in Google Drive (your storage) or your browser's local storage.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-emerald-400 mt-0.5">✓</span>
              <span><strong className="text-white">We store a small library index on our servers</strong> — file IDs, file names, MIME types, and timestamps — so your library persists across sessions and devices.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-emerald-400 mt-0.5">✓</span>
              <span><strong className="text-white">Media streams directly from Google Drive to your device</strong> using your OAuth permission. We proxy thumbnails to avoid browser CORS issues, but we don't store them.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-emerald-400 mt-0.5">✓</span>
              <span><strong className="text-white">AI features send only the minimum data needed</strong> to the AI provider (Google Gemini). We don't use your data to train models.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-emerald-400 mt-0.5">✓</span>
              <span><strong className="text-white">Cloud backup is optional and opt-in.</strong> By default, files stay in your browser only. If you enable Cloud Backup (Pro/Power), we store a copy on our infrastructure so you can access it across devices; you can turn it off anytime.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-emerald-400 mt-0.5">✓</span>
              <span><strong className="text-white">We don't sell your data.</strong> Ever.</span>
            </li>
          </ul>
        </section>

        {/* ── 2. Who we are ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">Who we are</h2>
          <p>
            Refract is operated by Alejandro Jackson. You can reach us at{' '}
            <a className="text-blue-400 hover:text-blue-300" href="mailto:userefractapp@gmail.com">userefractapp@gmail.com</a>.
            The service is available at{' '}
            <a className="text-blue-400 hover:text-blue-300" href="https://userefract.com">userefract.com</a>.
          </p>
        </section>

        {/* ── 3. What we collect ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">What we collect and why</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-white mb-1">Account information</h3>
              <p className="text-sm">
                When you sign in with Google, we receive your name, email, and profile photo.
                We use this for authentication and account identification.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-white mb-1">Library index (stored on our servers)</h3>
              <p className="text-sm">
                When you add media from Google Drive, we store a lightweight index of your selections:
                file IDs, file names, MIME types, file sizes, modification dates, and the date you added them.
                This is roughly 500 bytes per file — just enough to remember what you picked,
                so you don't have to re-select thousands of files every time you sign in.
              </p>
              <p className="text-sm mt-2 text-gray-400">
                This index contains <strong className="text-gray-200">no image data, no video data, and no file content</strong>.
                It's comparable to a table of contents — it tells us which pages exist, not what's on them.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-white mb-1">App settings and subscription info</h3>
              <p className="text-sm">
                Your preferences (shuffle interval, media filter), subscription tier, and collections are stored
                server-side so they persist across sessions.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-white mb-1">Opt-in cloud backup</h3>
              <p className="text-sm">
                <strong className="text-white">By default, your media and library data stay only on your device.</strong> Refract offers an optional Cloud Backup feature (available on Pro and Power plans) so you can access your library from other devices. Cloud Backup is <strong className="text-white">off by default</strong> and must be explicitly enabled in settings.
              </p>
              <p className="text-sm mt-2">
                If you enable Cloud Backup, we store the following on our infrastructure (Vercel Blob):
              </p>
              <ul className="list-inside list-disc text-sm mt-2 space-y-1 text-gray-400">
                <li>Backup copies of files you have chosen to sync (e.g. local uploads and metadata needed to restore your library)</li>
                <li>Data is stored per-account, encrypted in transit and at rest, and is used only to provide cross-device access</li>
              </ul>
              <p className="text-sm mt-2 text-gray-400">
                You can disable Cloud Backup at any time in settings. When you disable it, we stop adding new backup data and immediately delete the backup files we were holding for you.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-white mb-1">Local device storage (your browser)</h3>
              <p className="text-sm">
                Refract stores data locally in your browser (IndexedDB) to enable fast loading and offline access:
              </p>
              <ul className="list-inside list-disc text-sm mt-2 space-y-1 text-gray-400">
                <li>Authentication tokens (to keep you signed in)</li>
                <li>A local copy of your library metadata (for instant startup)</li>
                <li>Locally uploaded files (the actual file blobs — these never leave your device)</li>
                <li>Cached thumbnails and processed images</li>
                <li>AI search tag cache (to avoid redundant API calls)</li>
              </ul>
              <p className="text-sm mt-2 text-gray-400">
                You can clear all local data at any time by signing out or clearing your browser storage. Files stored locally are <strong className="text-gray-200">not</strong> uploaded to our servers unless you opt in to Cloud Backup.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-white mb-1">AI features (optional)</h3>
              <p className="text-sm">
                If you use AI-powered search, Refract sends selected images and search prompts to Google Gemini
                to generate descriptions and tags. We send only what's needed for the feature you requested.
                These calls are made on your behalf and are not used to train AI models.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-white mb-1">Thumbnail proxy</h3>
              <p className="text-sm">
                Google Drive thumbnails are fetched through a lightweight server-side proxy to avoid browser CORS restrictions.
                The proxy streams the thumbnail response — it does not store your images.
              </p>
            </div>

            <div>
              <h3 className="text-base font-semibold text-white mb-1">Standard web data</h3>
              <p className="text-sm">
                Like most web services, we collect basic usage data: browser type, pages viewed, IP address,
                error logs (via Sentry), and session cookies. This helps us keep the service running and fix bugs.
              </p>
            </div>
          </div>
        </section>

        {/* ── 4. What we DON'T do ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">What we don't do</h2>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-3">
              <span className="text-rose-400 mt-0.5">✕</span>
              <span>We <strong className="text-white">don't upload, store, or cache your photos or videos</strong> on our servers unless you explicitly enable Cloud Backup (Pro/Power). By default, media stays in your browser or Google Drive only.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-400 mt-0.5">✕</span>
              <span>We <strong className="text-white">don't sell your personal information</strong> to anyone.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-400 mt-0.5">✕</span>
              <span>We <strong className="text-white">don't use your data to train AI models</strong>.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-rose-400 mt-0.5">✕</span>
              <span>We <strong className="text-white">don't track your viewing habits</strong> for advertising.</span>
            </li>
          </ul>
        </section>

        {/* ── 5. Google API compliance ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">Google API Services</h2>
          <p className="text-sm">
            Refract's use and transfer of information received from Google APIs complies with the{' '}
            <a className="text-blue-400 hover:text-blue-300" href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
              Google API Services User Data Policy
            </a>, including the Limited Use requirements.
          </p>
          <ul className="list-inside list-disc text-sm mt-2 space-y-1 text-gray-400">
            <li>We use Google user data only to provide features you request.</li>
            <li>We do not use Google user data for advertising.</li>
            <li>We do not allow humans to read your Google data except for security, support (with your consent), or legal compliance.</li>
          </ul>
        </section>

        {/* ── 6. How we share info ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">How we share information</h2>
          <p className="text-sm mb-2">We don't sell your data. We share information only with:</p>
          <ul className="list-inside list-disc text-sm space-y-1 text-gray-400">
            <li><strong className="text-gray-200">Infrastructure providers</strong> — Vercel (hosting, serverless functions, blob storage for the library index and, if you enable it, Cloud Backup), Sentry (error monitoring).</li>
            <li><strong className="text-gray-200">AI provider</strong> — Google Gemini, only when you use AI features.</li>
            <li><strong className="text-gray-200">Legal requirements</strong> — if required by law or to protect safety.</li>
          </ul>
        </section>

        {/* ── Lawful requests and policy enforcement ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">Lawful requests and policy enforcement</h2>
          <p className="text-sm mb-2">
            We process and retain the user data described in this policy (including account information, file IDs, file names, MIME types, timestamps, and other metadata in your library index) to provide the Service. This data may also be used or disclosed when we are required or permitted to do so by law, or when necessary to enforce our Terms of Service and Acceptable Use Policy.
          </p>
          <ul className="list-inside list-disc text-sm space-y-1 text-gray-400 mb-2">
            <li><strong className="text-gray-200">Lawful requests:</strong> We may disclose user data (including metadata and identifiers) in response to valid legal process, including subpoenas, court orders, search warrants, DMCA notices, and requests from law enforcement or other government authorities. We will evaluate each request and respond as required or permitted by applicable law.</li>
            <li><strong className="text-gray-200">Policy enforcement:</strong> If we reasonably believe that your use of the Service violates our Terms of Service or Acceptable Use Policy (including prohibited content or conduct), we may suspend or terminate your account, remove or disable access to content or metadata associated with your account, and retain relevant data for the purpose of enforcing our policies, resolving disputes, or complying with legal obligations.</li>
            <li><strong className="text-gray-200">Cooperation:</strong> We may cooperate with authorities in jurisdictions where we operate (including under frameworks such as the EU Digital Services Act or UK Online Safety Act) to remove or restrict access to illegal content and to protect users. We do not disclose the contents of your media files to third parties except as necessary to comply with law or to enforce our policies where we have stored or processed such content.</li>
          </ul>
          <p className="text-sm text-gray-400">
            We do not scan or store the actual content of your photos and videos on our servers unless you have opted in to Cloud Backup; otherwise we store only metadata and identifiers. When we receive a valid report or legal notice regarding specific files (e.g., by file ID or description), we may remove or disable access to the corresponding entries in your library index (and, if applicable, Cloud Backup) and take other steps described in our Terms of Service.
          </p>
        </section>

        {/* ── 7. Data retention ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">Data retention</h2>
          <ul className="list-inside list-disc text-sm space-y-1 text-gray-400">
            <li><strong className="text-gray-200">Library index:</strong> retained while your account is active. Deleting your account removes it immediately.</li>
            <li><strong className="text-gray-200">Cloud Backup data:</strong> if you enabled Cloud Backup, backup files are retained until you disable the feature or delete your account. Both delete them immediately.</li>
            <li><strong className="text-gray-200">Local browser data:</strong> stored until you sign out or clear browser storage.</li>
            <li><strong className="text-gray-200">Error logs:</strong> retained for up to 90 days for debugging.</li>
          </ul>
        </section>

        {/* ── 8. Your rights ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">Your rights and choices</h2>
          <div className="space-y-3 text-sm">
            <p>
              <strong className="text-white">Revoke Google access:</strong> Go to your{' '}
              <a className="text-blue-400 hover:text-blue-300" href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
                Google account settings
              </a>{' '}
              → Third-party access → Remove Refract. We can no longer access your Drive after revocation.
            </p>
            <p>
              <strong className="text-white">Delete your account:</strong> You can permanently delete your
              account and all associated data at any time — your library index, collections, settings,
              cloud backup files, and connected-account tokens. Deletion is immediate and cannot be undone.
              Any active subscription is cancelled as part of the process. You can also email{' '}
              <a className="text-blue-400 hover:text-blue-300" href="mailto:userefractapp@gmail.com">userefractapp@gmail.com</a>{' '}
              if you would rather we handle it for you.
            </p>
            <p>
              <strong className="text-white">Disable Cloud Backup:</strong> Turn off Cloud Backup in Refract settings. This stops syncing and immediately, permanently deletes the backup files we hold for you.
            </p>
            <p>
              <strong className="text-white">Clear local data:</strong> Sign out of Refract, or clear your browser's site data.
            </p>
            <p className="text-gray-400">
              Depending on your location (EEA, UK, California, etc.), you may have additional rights including access, correction,
              portability, and objection. Contact us to exercise them.
            </p>
          </div>
        </section>

        {/* ── 9. Security ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">Security</h2>
          <p className="text-sm">
            All traffic uses HTTPS. Server-side data is stored in encrypted infrastructure (Vercel KV and Blob Storage).
            Authentication uses Google OAuth — we never see or store your Google password.
            No method of storage is 100% secure, but we take reasonable measures to protect your data.
          </p>
        </section>

        {/* ── 10. Children ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">Children</h2>
          <p className="text-sm">
            Refract is not intended for children under 13. We don't knowingly collect data from children.
            If you believe a child has provided personal data, contact us and we'll delete it.
          </p>
        </section>

        {/* ── 11. Changes ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">Changes to this policy</h2>
          <p className="text-sm">
            We may update this policy from time to time. We'll update the "Last updated" date and may notify you
            of material changes. Continued use of Refract after changes means you accept the revised policy.
          </p>
        </section>

        {/* ── 12. Contact ── */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-white mb-3">Contact</h2>
          <p className="text-sm">
            Questions or requests? Email{' '}
            <a className="text-blue-400 hover:text-blue-300" href="mailto:userefractapp@gmail.com">userefractapp@gmail.com</a>.
          </p>
        </section>
      </main>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
